import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import RecordHistoryDialog from "@/components/hrms/RecordHistoryDialog";
import { AttendanceCard } from "@/components/hrms/AttendanceCard";
import { AttendancePolicies } from "@/components/hrms/AttendancePolicies";
import { BreakTrackerPanel, LiveListDialog, WorkingHoursPanel } from "@/components/hrms/AttendancePanels";
import { AssignmentDialog, EmployeeOnboardDialog } from "@/components/hrms/PeopleDialogs";
import { LatestAnnouncements, TeamLivePanel } from "@/components/hrms/TeamLivePanel";

const shiftChannel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("nexreq-hrms") : null;
const NIGHT_PRESET = {
  name: "Night Shift",
  timezone: "Asia/Kolkata",
  start_time: "20:00",
  end_time: "05:00",
  allowed_breaks: 2,
  break_allowance_minutes: 60,
  break_grace_minutes: 10,
  late_threshold_minutes: 15,
};
import EmployeeAvatar from "@/components/communications/EmployeeAvatar";
import { toast } from "sonner";
import {
  BriefcaseBusiness,
  CalendarDays,
  Clock3,
  Coffee,
  FileText,
  UsersRound,
} from "lucide-react";

const EMPTY_LEAVE = {
  leave_type: "annual",
  start_date: "",
  end_date: "",
  half_day: false,
  reason: "",
};
const EMPTY_REQUEST = {
  request_type: "wfh",
  request_date: "",
  reason: "",
  details: {},
};
const EMPTY_SHIFT = {
  name: "",
  start_time: "",
  end_time: "",
  timezone: "Asia/Kolkata",
  break_allowance_minutes: 60,
  max_break_minutes: 60,
  allowed_breaks: 2,
  break_grace_minutes: 10,
  grace_minutes: 0,
  late_threshold_minutes: 15,
  early_logout_minutes: 0,
  overtime_after_minutes: 0,
  status: "active",
};
const TIMEZONES = [
  ["Asia/Kolkata", "IST — India"],
  ["America/Los_Angeles", "PST — US Pacific"],
  ["Asia/Dubai", "GST — Gulf"],
  ["UTC", "UTC"],
];
const EMPTY_EMPLOYEE = {
  user_id: "",
  employee_id: "",
  designation: "",
  department: "",
  team_id: "none",
  joining_date: "",
  employment_type: "full_time",
  location: "",
  shift_id: "none",
};

export default function HRMS() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [requests, setRequests] = useState([]);
  const [teamMonitor, setTeamMonitor] = useState(null);
  const [report, setReport] = useState(null);
  const [accessList, setAccessList] = useState([]);
  const [reportRange, setReportRange] = useState({ start: "", end: "" });
  const [breakTracker, setBreakTracker] = useState(null);
  const [workingHours, setWorkingHours] = useState(null);
  const [whRange, setWhRange] = useState({ start: "", end: "" });
  const [payslips, setPayslips] = useState([]);
  const [salaryEmp, setSalaryEmp] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [tab, setTab] = useState("overview");
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [shiftOpen, setShiftOpen] = useState(false);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [assignmentOptions, setAssignmentOptions] = useState({
    users: [],
    teams: [],
    departments: [],
  });
  const [assignment, setAssignment] = useState({
    shift_id: "",
    target_type: "team",
    target_id: "",
  });
  const [leave, setLeave] = useState(EMPTY_LEAVE);
  const [employeeRequest, setEmployeeRequest] = useState(EMPTY_REQUEST);
  const [shift, setShift] = useState(EMPTY_SHIFT);
  const [saving, setSaving] = useState(false);
  const [employeeImportOpen, setEmployeeImportOpen] = useState(false);
  const [employeeImportFile, setEmployeeImportFile] = useState(null);
  const [employeeCreateOpen, setEmployeeCreateOpen] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState({
    users: [],
    teams: [],
    shifts: [],
  });
  const [employeeDraft, setEmployeeDraft] = useState(EMPTY_EMPLOYEE);
  const [editingShift, setEditingShift] = useState(null);
  const [liveFilter, setLiveFilter] = useState(null);
  const [employeeFilter, setEmployeeFilter] = useState("all");
  const [requestFilter, setRequestFilter] = useState("all");
  const [selected, setSelected] = useState([]);
  const [assignTargets, setAssignTargets] = useState(null);
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const isSuperAdmin = ["super_admin", "founder"].includes(user?.role);
  const canConfigureShifts =
    isSuperAdmin || ["hr", "hr_admin"].includes(user?.role);
  const canViewShifts =
    canConfigureShifts ||
    ["team_admin", "team_lead", "ra_admin", "recruitment_manager"].includes(
      user?.role,
    );
  const canMonitorTeam = [
    "founder",
    "super_admin",
    "hr",
    "hr_admin",
    "team_admin",
    "team_lead",
    "ra_admin",
    "recruitment_manager",
  ].includes(user?.role);
  const canPayroll =
    [
      "super_admin",
      "founder",
      "administrator",
      "hr",
      "hr_admin",
      "finance",
      "payroll_admin",
    ].includes(user?.role) &&
    (user?.hrms_permission_matrix?.payroll?.create ?? true);
  const canManageEmployees =
    canPayroll ||
    ["super_admin", "founder", "hr", "hr_admin"].includes(user?.role);

  const load = useCallback(async () => {
    try {
      const [dash, mine, employeeList, leaveList, requestList] =
        await Promise.all([
          api.get("/hrms/dashboard"),
          api.get("/hrms/me/attendance"),
          api.get("/hrms/employees"),
          api.get("/hrms/leaves"),
          api.get("/hrms/requests"),
        ]);
      setDashboard(dash.data);
      setAttendance(mine.data);
      setEmployees(employeeList.data.items || []);
      setLeaves(leaveList.data.items || []);
      setRequests(requestList.data.items || []);
      if (canViewShifts) {
        const shiftList = await api.get("/hrms/shifts");
        setShifts(shiftList.data.items || []);
      }
      if (canConfigureShifts) {
        const options = await api.get("/hrms/shift-assignments/options");
        setAssignmentOptions(options.data);
      }
      if (canManageEmployees) {
        const employeeOptionsResponse = await api.get(
          "/hrms/employees/options",
        );
        setEmployeeOptions(employeeOptionsResponse.data);
      }
      if (canMonitorTeam) {
        const [monitor, bt, wh] = await Promise.all([
          api.get("/hrms/team-monitor"),
          api.get("/hrms/break-tracker"),
          api.get("/hrms/reports/working-hours", {
            params: {
              start: whRange.start || undefined,
              end: whRange.end || undefined,
            },
          }),
        ]);
        setTeamMonitor(monitor.data);
        setBreakTracker(bt.data);
        setWorkingHours(wh.data);
      }
      const reportResp = await api.get("/hrms/reports/attendance", {
        params: {
          start: reportRange.start || undefined,
          end: reportRange.end || undefined,
        },
      });
      setReport(reportResp.data);
      if (isSuperAdmin) {
        const access = await api.get("/hrms/attendance-access");
        setAccessList(access.data.items || []);
      }
      const slips = await api.get("/hrms/payroll/payslips");
      setPayslips(slips.data.items || []);
    } catch (error) {
      toast.error(apiErr(error));
    }
  }, [
    canConfigureShifts,
    canManageEmployees,
    canMonitorTeam,
    canViewShifts,
    isSuperAdmin,
    reportRange,
    whRange,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = setInterval(() => {
      api.get("/hrms/me/attendance").then(({ data }) => setAttendance(data)).catch(() => {});
    }, 30000);
    const onShift = () => {
      load();
      setRefreshKey((k) => k + 1);
    };
    shiftChannel?.addEventListener("message", onShift);
    return () => {
      clearInterval(timer);
      shiftChannel?.removeEventListener("message", onShift);
    };
  }, [load]);

  const openCard = (key) => {
    if (["working", "on_break", "present", "late", "on_leave", "absent", "clocked_out", "not_started"].includes(key)) {
      setLiveFilter(key);
      return;
    }
    if (key === "pending") {
      setRequestFilter("pending");
      setTab("leave_requests");
      return;
    }
    setEmployeeFilter(key);
    setTab("employees");
  };
  const today = new Date().toISOString().slice(0, 10);
  const visibleEmployees = employees.filter((e) =>
    employeeFilter === "joiners" ? e.joining_date === today
      : employeeFilter === "exits" ? e.employment_status === "exited" : true,
  );

  const attendanceAction = async (action) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/hrms/me/attendance/${action}`);
      setAttendance(data);
      load();
      setRefreshKey((k) => k + 1);
      return data;
    } catch (error) {
      toast.error(apiErr(error));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const createLeave = async () => {
    setSaving(true);
    try {
      await api.post("/hrms/leaves", leave);
      toast.success("Leave request submitted");
      setLeaveOpen(false);
      setLeave(EMPTY_LEAVE);
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  const createRequest = async () => {
    setSaving(true);
    try {
      await api.post("/hrms/requests", employeeRequest);
      toast.success("Request submitted");
      setRequestOpen(false);
      setEmployeeRequest(EMPTY_REQUEST);
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  const createShift = async () => {
    const problems = [];
    if (!shift.name.trim()) problems.push("Shift name is required");
    if (!shift.start_time || !shift.end_time) problems.push("Start and end time are required");
    if (shift.start_time && shift.start_time === shift.end_time) problems.push("Start and end time cannot be the same");
    if (!(shift.allowed_breaks >= 0 && shift.allowed_breaks <= 10)) problems.push("Breaks per day must be 0–10");
    if (problems.length) {
      toast.error(problems.join(". "));
      return;
    }
    setSaving(true);
    try {
      if (editingShift) {
        await api.put(`/hrms/shifts/${editingShift.id}`, shift);
        toast.success("Shift updated");
      } else {
        await api.post("/hrms/shifts", shift);
        toast.success("Shift created");
      }
      setShiftOpen(false);
      setShift(EMPTY_SHIFT);
      setEditingShift(null);
      shiftChannel?.postMessage("shift-updated");
      setRefreshKey((k) => k + 1);
      api.get("/hrms/me/attendance").then(({ data }) => setAttendance(data)).catch(() => {});
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  const assignShift = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/hrms/shift-assignments", assignment);
      toast.success(`${data.assigned_count} employee(s) assigned`);
      setAssignmentOpen(false);
      setAssignment({ shift_id: "", target_type: "team", target_id: "" });
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  const decide = async (collection, id, decision) => {
    try {
      await api.post(`/hrms/${collection}/${id}/decision`, {
        decision,
        comment: "",
        reason: "",
      });
      toast.success(`Request ${decision}`);
      load();
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  const toggleAccess = async (userId, enabled) => {
    try {
      await api.put(`/hrms/attendance-access/${userId}`, { enabled });
      setAccessList((list) =>
        list.map((person) =>
          person.id === userId
            ? { ...person, attendance_enabled: enabled }
            : person,
        ),
      );
      toast.success(enabled ? "Attendance enabled" : "Attendance disabled");
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  const runPayroll = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.post("/hrms/payroll/runs", payload);
      toast.success(`Payroll calculated for ${data.count} employee(s)`);
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  const saveSalary = async (payload) => {
    setSaving(true);
    try {
      await api.put(`/hrms/employees/${salaryEmp.id}/salary`, payload);
      toast.success("Salary updated");
      setSalaryEmp(null);
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  const exportEmployees = async () => {
    try {
      const response = await api.get("/hrms/employees/export/csv", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "text/csv" }),
      );
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "nexreq-employees.csv";
      anchor.click();
      window.URL.revokeObjectURL(url);
      toast.success("Employee export downloaded");
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  const importEmployees = async () => {
    if (!employeeImportFile) return;
    setSaving(true);
    try {
      const data = new FormData();
      data.append("file", employeeImportFile);
      const response = await api.post("/hrms/employees/import/csv", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${response.data.created} employee profile(s) imported`);
      setEmployeeImportOpen(false);
      setEmployeeImportFile(null);
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  const createEmployee = async () => {
    if (!employeeDraft.employee_id.trim()) {
      toast.error("Employee ID is required");
      return;
    }
    if (!employeeDraft.user_id) {
      toast.error("Select an internal NEXREQ user account");
      return;
    }
    setSaving(true);
    try {
      await api.post("/hrms/employees", {
        ...employeeDraft,
        team_id:
          employeeDraft.team_id === "none" ? null : employeeDraft.team_id,
        shift_id:
          employeeDraft.shift_id === "none" ? null : employeeDraft.shift_id,
      });
      toast.success("Internal employee profile created");
      setEmployeeCreateOpen(false);
      setEmployeeDraft(EMPTY_EMPLOYEE);
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="hrms-page">
      <PageHeader
        title="People & HRMS"
        subtitle={
          "Employee operations, attendance, people records, leave, requests, and team " +
          "achievement."
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => navigate("/hrms/attendance")}
              data-testid="hrms-attendance-management-button"
            >
              Attendance hub
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/hrms/leave-management")}
              data-testid="hrms-leave-management-button"
            >
              Leave hub
            </Button>
            {canPayroll && (
              <Button
                variant="outline"
                onClick={() => navigate("/hrms/payroll")}
                data-testid="hrms-payroll-workspace-button"
              >
                Payroll workspace
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setLeaveOpen(true)}
              data-testid="hrms-request-leave-button"
            >
              <CalendarDays className="mr-1.5 h-4 w-4" /> Request leave
            </Button>
            <Button
              onClick={() => setRequestOpen(true)}
              data-testid="hrms-request-center-button"
              className="bg-[#255058] text-white hover:bg-[#1c3d43]"
            >
              <FileText className="mr-1.5 h-4 w-4" /> New request
            </Button>
          </div>
        }
      />

      <AttendanceCard
        attendance={attendance}
        saving={saving}
        onAction={attendanceAction}
        onChanged={load}
      />

      <div
        className="grid grid-cols-2 gap-3 lg:grid-cols-4"
        data-testid="hrms-dashboard-metrics"
      >
        <Metric
          label="Employees"
          value={dashboard?.employees}
          icon={UsersRound}
          onClick={() => openCard("all")}
        />
        <Metric
          label="Working now"
          value={dashboard?.attendance?.working}
          icon={BriefcaseBusiness}
          onClick={() => openCard("working")}
        />
        <Metric
          label="On break"
          value={dashboard?.attendance?.on_break}
          icon={Coffee}
          onClick={() => openCard("on_break")}
        />
        <Metric
          label="Pending requests"
          value={
            (dashboard?.pending_requests || 0) + (dashboard?.pending_leave || 0)
          }
          icon={FileText}
          onClick={() => openCard("pending")}
        />
      </div>

      <div
        className="flex flex-wrap gap-2 border-b border-border pb-3"
        data-testid="hrms-tabs"
      >
        {[
          "overview",
          "employees",
          "leave_requests",
          "break_tracker",
          "working_hours",
          "payroll",
          "reports",
          "attendance_access",
          "shifts",
          "team_activity",
        ]
          .filter((value) => value !== "team_activity" || canMonitorTeam)
          .filter((value) => value !== "shifts" || canViewShifts)
          .filter(
            (value) => value !== "attendance_access" || isSuperAdmin,
          )
          .map((value) => (
            <Button
              key={value}
              variant={tab === value ? "default" : "outline"}
              size="sm"
              onClick={() => setTab(value)}
              data-testid={`hrms-tab-${value}`}
              className={tab === value ? "bg-[#255058] hover:bg-[#1c3d43]" : ""}
            >
              {labelForTab(value)}
            </Button>
          ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-4">
          <Overview dashboard={dashboard} onOpen={openCard} />
          <LatestAnnouncements />
        </div>
      )}
      {tab === "employees" && (
        <EmployeeTable
          employees={visibleEmployees}
          filter={employeeFilter}
          onClearFilter={() => setEmployeeFilter("all")}
          selected={selected}
          onToggle={(id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))}
          onAssign={(rows) => setAssignTargets(rows)}
          onLink={() => setEmployeeCreateOpen(true)}
          canPayroll={canPayroll}
          canManage={canManageEmployees}
          onEdit={setSalaryEmp}
          onHistory={(id) => setHistoryTarget({ kind: "employee", id })}
          onOpen={(id) => navigate(`/hrms/employees/${id}`)}
          onExport={exportEmployees}
          onImport={() => setEmployeeImportOpen(true)}
          onAdd={() => setOnboardOpen(true)}
        />
      )}
      {tab === "leave_requests" && (
        <div className="space-y-3">
          <div className="flex gap-2" data-testid="hrms-request-filter">
            {["all", "pending"].map((value) => (
              <Button key={value} size="sm" variant={requestFilter === value ? "default" : "outline"}
                className={requestFilter === value ? "bg-[#255058] hover:bg-[#1c3d43]" : ""}
                onClick={() => setRequestFilter(value)} data-testid={`hrms-request-filter-${value}`}>
                {value === "all" ? "All" : "Pending"}
              </Button>
            ))}
          </div>
          <RequestTables
            leaves={requestFilter === "pending" ? leaves.filter((r) => r.status === "pending") : leaves}
            requests={requestFilter === "pending" ? requests.filter((r) => r.status === "pending") : requests}
            user={user}
            onDecision={decide}
            onHistory={(id) => setHistoryTarget({ kind: "leave", id })}
          />
        </div>
      )}
      {tab === "shifts" && (
        <div className="space-y-5">
        <ShiftTable
          shifts={shifts}
          canConfigure={canConfigureShifts}
          onCreate={() => {
            setEditingShift(null);
            setShift(EMPTY_SHIFT);
            setShiftOpen(true);
          }}
          onEdit={(item) => {
            setEditingShift(item);
            setShift({ ...EMPTY_SHIFT, ...item });
            setShiftOpen(true);
          }}
          onAssign={() => setAssignmentOpen(true)}
        />
        {isSuperAdmin && <AttendancePolicies />}
        </div>
      )}
      {tab === "reports" && (
        <AttendanceReport
          report={report}
          range={reportRange}
          onRange={setReportRange}
          onHistory={(id) => setHistoryTarget({ kind: "attendance", id })}
        />
      )}
      {tab === "attendance_access" && (
        <AttendanceAccess people={accessList} onToggle={toggleAccess} />
      )}
      {tab === "team_activity" && (
        <div className="space-y-4">
          <TeamLivePanel />
          <TeamMonitor monitor={teamMonitor} />
        </div>
      )}
      {tab === "break_tracker" && <BreakTrackerPanel refreshKey={refreshKey} />}
      {tab === "working_hours" && <WorkingHoursPanel refreshKey={refreshKey} />}
      <LiveListDialog filter={liveFilter} onClose={() => setLiveFilter(null)} />
      <EmployeeOnboardDialog open={onboardOpen} onClose={() => setOnboardOpen(false)} onSaved={load} />
      <AssignmentDialog
        targets={assignTargets}
        onClose={() => setAssignTargets(null)}
        onSaved={() => {
          setSelected([]);
          load();
        }}
      />
      {tab === "payroll" && (
        <Payroll
          payslips={payslips}
          saving={saving}
          onRun={runPayroll}
          canRun={canPayroll}
          onHistory={(id) => setHistoryTarget({ kind: "payroll", id })}
        />
      )}

      <LeaveDialog
        open={leaveOpen}
        value={leave}
        saving={saving}
        onChange={setLeave}
        onClose={() => setLeaveOpen(false)}
        onSubmit={createLeave}
      />
      <RequestDialog
        open={requestOpen}
        value={employeeRequest}
        saving={saving}
        onChange={setEmployeeRequest}
        onClose={() => setRequestOpen(false)}
        onSubmit={createRequest}
      />
      <ShiftDialog
        open={shiftOpen}
        value={shift}
        editing={editingShift}
        saving={saving}
        onChange={setShift}
        onClose={() => setShiftOpen(false)}
        onSubmit={createShift}
      />
      <EmployeeCreateDialog
        open={employeeCreateOpen}
        options={employeeOptions}
        saving={saving}
        value={employeeDraft}
        onChange={setEmployeeDraft}
        onClose={() => setEmployeeCreateOpen(false)}
        onSubmit={createEmployee}
      />
      <ShiftAssignmentDialog
        open={assignmentOpen}
        options={assignmentOptions}
        saving={saving}
        value={assignment}
        onChange={setAssignment}
        onClose={() => setAssignmentOpen(false)}
        onSubmit={assignShift}
      />
      <SalaryDialog
        emp={salaryEmp}
        saving={saving}
        onClose={() => setSalaryEmp(null)}
        onSave={saveSalary}
        onHistory={(id) => setHistoryTarget({ kind: "employee", id })}
      />
      <RecordHistoryDialog
        target={historyTarget}
        onClose={() => setHistoryTarget(null)}
      />
      <Dialog
        open={employeeImportOpen}
        onOpenChange={(open) => !open && setEmployeeImportOpen(false)}
      >
        <DialogContent data-testid="employee-import-dialog">
          <DialogHeader>
            <DialogTitle>Import employee profiles</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="employee-import-file">CSV file</Label>
            <Input
              id="employee-import-file"
              type="file"
              accept=".csv,text/csv"
              onChange={(event) =>
                setEmployeeImportFile(event.target.files?.[0] || null)
              }
              data-testid="employee-import-file-input"
            />
            <p className="text-sm text-muted-foreground">
              Export the current employee list to use the supported column
              structure.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmployeeImportOpen(false)}
              data-testid="employee-import-cancel-button"
            >
              Cancel
            </Button>
            <Button
              disabled={saving || !employeeImportFile}
              onClick={importEmployees}
              data-testid="employee-import-submit-button"
              className="bg-[#255058] text-white hover:bg-[#1c3d43]"
            >
              Import CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Overview({ dashboard, onOpen }) {
  return (
    <Card className="p-5" data-testid="hrms-overview-card">
      <div className="font-display text-lg font-semibold">
        Team attendance overview
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
        <Inline label="Present" value={dashboard?.attendance?.present || 0} onClick={() => onOpen("present")} />
        <Inline label="Working now" value={dashboard?.attendance?.working || 0} onClick={() => onOpen("working")} />
        <Inline label="On break" value={dashboard?.attendance?.on_break || 0} onClick={() => onOpen("on_break")} />
        <Inline label="Clocked out" value={dashboard?.attendance?.clocked_out || 0} onClick={() => onOpen("clocked_out")} />
        <Inline label="Late" value={dashboard?.attendance?.late || 0} onClick={() => onOpen("late")} />
        <Inline label="Absent" value={dashboard?.attendance?.absent || 0} onClick={() => onOpen("absent")} />
        <Inline label="On leave" value={dashboard?.attendance?.leave || 0} onClick={() => onOpen("on_leave")} />
        <Inline label="Not started" value={dashboard?.attendance?.not_started || 0} onClick={() => onOpen("not_started")} />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Inline label="New joiners" value={dashboard?.new_joiners || 0} onClick={() => onOpen("joiners")} />
        <Inline label="Exits" value={dashboard?.exits || 0} onClick={() => onOpen("exits")} />
        <Inline
          label="Scope"
          value={dashboard?.scope === "company" ? "Company" : "Team / Self"}
        />
      </div>
    </Card>
  );
}

function TeamMonitor({ monitor }) {
  if (!monitor) {
    return (
      <Card className="p-5" data-testid="hrms-team-monitor-loading">
        Loading team activity…
      </Card>
    );
  }
  return (
    <div className="space-y-5" data-testid="hrms-team-monitor">
      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-display text-lg font-semibold">
              Team activity monitor
            </div>
            <div className="text-sm text-muted-foreground">
              Scope:{" "}
              {monitor.scope === "company" ? "Company-wide" : "Assigned team"}
            </div>
          </div>
          <div className="flex gap-2 text-sm">
            <StatusBadge
              value={`${monitor.pending_leave.length} leave requests`}
            />
            <StatusBadge
              value={`${monitor.pending_requests.length} team requests`}
            />
          </div>
        </div>
      </Card>
      <Card
        className="overflow-hidden"
        data-testid="hrms-team-attendance-table"
      >
        <div className="border-b border-border px-5 py-4 font-display text-lg font-semibold">
          Today’s team attendance
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Team</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Login</TableHead>
                <TableHead>Worked</TableHead>
                <TableHead>Break</TableHead>
                <TableHead>Late</TableHead>
                <TableHead>Logout / gaps</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {monitor.attendance.map((record) => (
                <TableRow
                  key={record.id}
                  data-testid={`hrms-team-attendance-${record.id}`}
                >
                  <TableCell className="font-medium">
                    {record.employee_name}
                  </TableCell>
                  <TableCell>{record.team_name || "—"}</TableCell>
                  <TableCell>
                    <StatusBadge value={record.current_status} />
                  </TableCell>
                  <TableCell>{formatTime(record.login_at)}</TableCell>
                  <TableCell>{minutes(record.working_minutes)}</TableCell>
                  <TableCell>
                    {minutes(record.break_minutes)} · {record.break_count}/
                    {record.breaks_allowed}
                  </TableCell>
                  <TableCell>
                    {record.late_minutes ? `${record.late_minutes} min` : "—"}
                  </TableCell>
                  <TableCell className="text-xs">
                    {formatTime(record.logout_at)}
                    {(record.gaps || []).map((gap) => (
                      <div key={gap.from} className="text-muted-foreground">
                        out {formatTime(gap.from)}–{formatTime(gap.to)}
                      </div>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
              {!monitor.attendance.length && (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="py-8 text-center text-sm text-muted-foreground"
                  >
                    No attendance activity is available today.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
      <div className="grid gap-5 xl:grid-cols-2">
        <MonitorList
          title="Pending leave approvals"
          rows={monitor.pending_leave}
          testId="hrms-monitor-leave"
        />
        <MonitorList
          title="Pending team requests"
          rows={monitor.pending_requests}
          testId="hrms-monitor-requests"
        />
      </div>
      <Card className="overflow-hidden" data-testid="hrms-team-audit-activity">
        <div className="border-b border-border px-5 py-4 font-display text-lg font-semibold">
          Recent team activity
        </div>
        <div className="divide-y divide-border">
          {monitor.activity.map((item) => (
            <div
              key={item.id}
              className="flex justify-between gap-4 px-5 py-3 text-sm"
            >
              <div>
                <span className="font-medium">{item.user_name}</span> ·{" "}
                {item.action.replace(/_/g, " ")}
              </div>
              <div className="shrink-0 text-xs text-muted-foreground">
                {formatTime(item.created_at)}
              </div>
            </div>
          ))}
          {!monitor.activity.length && (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">
              No team activity is available yet.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function MonitorList({ rows, testId, title }) {
  return (
    <Card className="p-5" data-testid={testId}>
      <div className="font-display text-lg font-semibold">{title}</div>
      <div className="mt-4 space-y-3">
        {rows.map((row) => (
          <div key={row.id} className="border-l-2 border-[#255058] pl-3">
            <div className="font-medium">{row.requester_name}</div>
            <div className="text-xs text-muted-foreground">
              {row.leave_type || row.request_type} · {row.reason}
            </div>
          </div>
        ))}
        {!rows.length && (
          <div className="text-sm text-muted-foreground">Nothing pending.</div>
        )}
      </div>
    </Card>
  );
}

function EmployeeTable({
  employees,
  filter,
  onClearFilter,
  selected = [],
  onToggle,
  onAssign,
  onLink,
  canManage,
  canPayroll,
  onEdit,
  onExport,
  onHistory,
  onImport,
  onAdd,
  onOpen,
}) {
  const money = (value) => (value ? `$${Number(value).toLocaleString()}` : "—");
  return (
    <Card className="overflow-hidden" data-testid="hrms-employees-table">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
        <div className="font-display text-lg font-semibold">
          Employee directory
          {filter && filter !== "all" && (
            <button type="button" onClick={onClearFilter} className="ml-2 rounded-full bg-[#255058]/10 px-2 py-0.5 text-xs font-normal" data-testid="employee-filter-chip">
              {filter === "joiners" ? "New joiners today" : "Exits"} ×
            </button>
          )}
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            {selected.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => onAssign(employees.filter((e) => selected.includes(e.user_id)))} data-testid="employee-bulk-assign-button">
                Reassign {selected.length} selected
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={onLink} data-testid="employee-link-existing-button">
              Link existing account
            </Button>
            <Button
              size="sm"
              onClick={onAdd}
              data-testid="employee-add-button"
              className="bg-[#255058] text-white hover:bg-[#1c3d43]"
            >
              Add employee
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onExport}
              data-testid="employee-export-button"
            >
              Export CSV
            </Button>
            <Button
              size="sm"
              onClick={onImport}
              data-testid="employee-import-button"
              className="bg-[#255058] text-white hover:bg-[#1c3d43]"
            >
              Import CSV
            </Button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {canManage && <TableHead className="w-8" />}
              <TableHead>Employee</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Team</TableHead>
              <TableHead>Reports to</TableHead>
              <TableHead>Shift</TableHead>
              <TableHead>Designation</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Base salary</TableHead>
              <TableHead>Profile</TableHead>
              {canPayroll && <TableHead>Salary</TableHead>}
              <TableHead>History</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {employees.map((employee) => (
              <TableRow
                key={employee.id}
                data-testid={`hrms-employee-row-${employee.id}`}
              >
                {canManage && (
                  <TableCell>
                    <input
                      type="checkbox"
                      disabled={!employee.user_id}
                      checked={selected.includes(employee.user_id)}
                      onChange={() => onToggle(employee.user_id)}
                      data-testid={`employee-select-${employee.id}`}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <div className="flex items-center gap-2">
                    <EmployeeAvatar
                      className="h-8 w-8"
                      fileId={employee.profile_photo_id}
                      name={employee.name || employee.employee_id}
                      testId={`employee-directory-avatar-${employee.id}`}
                    />
                    <div>
                      <div className="font-medium">
                        {employee.name || employee.employee_id}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {employee.email || employee.company_email}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>{employee.department || "—"}</TableCell>
                <TableCell>{employee.team_name || "—"}</TableCell>
                <TableCell>{employee.reports_to_name || "—"}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {employee.shift_name || "—"}
                    {canManage && employee.user_id && (
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => onAssign([employee])} data-testid={`employee-assign-${employee.id}`}>
                        Assign
                      </Button>
                    )}
                  </div>
                </TableCell>
                <TableCell>{employee.designation || "—"}</TableCell>
                <TableCell>
                  <StatusBadge value={employee.employment_status} />
                </TableCell>
                <TableCell>
                  {money(
                    employee.salary?.base_monthly ?? employee.salary?.base,
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onOpen(employee.id)}
                    data-testid={`employee-profile-open-${employee.id}`}
                  >
                    Open
                  </Button>
                </TableCell>
                {canPayroll && (
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onEdit(employee)}
                      data-testid={`edit-salary-${employee.id}`}
                    >
                      Edit
                    </Button>
                  </TableCell>
                )}
                <TableCell>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onHistory(employee.id)}
                    data-testid={`employee-history-${employee.id}`}
                  >
                    History
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!employees.length && (
              <TableRow>
                <TableCell
                  colSpan={canPayroll ? 12 : 11}
                  className="py-8 text-center text-sm text-muted-foreground"
                >
                  No employee profiles are visible in your scope.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function SalaryDialog({ emp, saving, onClose, onSave, onHistory }) {
  const [form, setForm] = useState({
    base_monthly: 0,
    overtime_hourly_rate: 0,
    allowances: [],
    deductions: [],
  });
  useEffect(() => {
    if (emp)
      setForm({
        base_monthly: emp.salary?.base_monthly ?? emp.salary?.base ?? 0,
        overtime_hourly_rate: emp.salary?.overtime_hourly_rate ?? 0,
        allowances: emp.allowances || [],
        deductions: emp.deductions || [],
      });
  }, [emp]);
  const setRow = (key, index, field, value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].map((row, idx) =>
        idx === index
          ? { ...row, [field]: field === "amount" ? Number(value) : value }
          : row,
      ),
    }));
  const addRow = (key) =>
    setForm((f) => ({ ...f, [key]: [...f[key], { label: "", amount: 0 }] }));
  const delRow = (key, index) =>
    setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== index) }));
  return (
    <Dialog open={Boolean(emp)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Salary · {emp?.name || emp?.employee_id}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label="Base salary (monthly)">
            <Input
              type="number"
              min="0"
              value={form.base_monthly}
              onChange={(e) =>
                setForm({ ...form, base_monthly: Number(e.target.value) })
              }
              data-testid="salary-base-input"
            />
          </Field>
          <Field label="Overtime hourly rate">
            <Input
              type="number"
              min="0"
              value={form.overtime_hourly_rate}
              onChange={(e) =>
                setForm({
                  ...form,
                  overtime_hourly_rate: Number(e.target.value),
                })
              }
              data-testid="salary-ot-input"
            />
          </Field>
          {["allowances", "deductions"].map((key) => (
            <div key={key}>
              <div className="flex items-center justify-between">
                <Label className="capitalize">{key}</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => addRow(key)}
                  data-testid={`salary-add-${key}`}
                >
                  Add
                </Button>
              </div>
              <div className="mt-1 space-y-2">
                {form[key].map((row, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      placeholder="Label"
                      value={row.label}
                      onChange={(e) =>
                        setRow(key, index, "label", e.target.value)
                      }
                      data-testid={`salary-${key}-label-${index}`}
                    />
                    <Input
                      type="number"
                      placeholder="Amount"
                      value={row.amount}
                      onChange={(e) =>
                        setRow(key, index, "amount", e.target.value)
                      }
                      className="w-32"
                      data-testid={`salary-${key}-amount-${index}`}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => delRow(key, index)}
                    >
                      ×
                    </Button>
                  </div>
                ))}
                {!form[key].length && (
                  <div className="text-xs text-muted-foreground">None</div>
                )}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onHistory(emp.id)}
            data-testid="salary-history-button"
          >
            History
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="salary-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              onSave({
                salary: {
                  base_monthly: form.base_monthly,
                  overtime_hourly_rate: form.overtime_hourly_rate,
                },
                allowances: form.allowances,
                deductions: form.deductions,
              })
            }
            disabled={saving}
            data-testid="salary-save"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            Save salary
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RequestTables({ leaves, requests, user, onDecision, onHistory }) {
  const canApprove = [
    "super_admin",
    "founder",
    "hr",
    "hr_admin",
    "team_admin",
    "team_lead",
    "ra_admin",
    "recruitment_manager",
  ].includes(user?.role);
  return (
    <div className="grid gap-5 xl:grid-cols-2">
      <RequestTable
        title="Leave requests"
        rows={leaves}
        type="leaves"
        canApprove={canApprove}
        onDecision={onDecision}
        onHistory={onHistory}
      />
      <RequestTable
        title="Employee requests"
        rows={requests}
        type="requests"
        canApprove={canApprove}
        onDecision={onDecision}
        onHistory={onHistory}
      />
    </div>
  );
}

function RequestTable({
  title,
  rows,
  type,
  canApprove,
  onDecision,
  onHistory,
}) {
  return (
    <Card className="overflow-hidden" data-testid={`hrms-${type}-table`}>
      <div className="border-b border-border px-5 py-4 font-display text-lg font-semibold">
        {title}
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Requester</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.requester_name}</TableCell>
                <TableCell>{row.leave_type || row.request_type}</TableCell>
                <TableCell>
                  <StatusBadge value={row.status} />
                </TableCell>
                <TableCell>
                  {canApprove && row.status === "pending" ? (
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDecision(type, row.id, "approved")}
                        data-testid={`hrms-${type}-approve-${row.id}`}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDecision(type, row.id, "rejected")}
                        data-testid={`hrms-${type}-reject-${row.id}`}
                      >
                        Reject
                      </Button>
                      {type === "leaves" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onHistory(row.id)}
                          data-testid={`hrms-${type}-history-${row.id}`}
                        >
                          History
                        </Button>
                      )}
                    </div>
                  ) : type === "leaves" ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onHistory(row.id)}
                      data-testid={`hrms-${type}-history-${row.id}`}
                    >
                      History
                    </Button>
                  ) : (
                    "—"
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="py-8 text-center text-sm text-muted-foreground"
                >
                  No requests in scope.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function ShiftTable({ shifts, canConfigure, onAssign, onCreate, onEdit }) {
  return (
    <Card className="overflow-hidden" data-testid="hrms-shifts-table">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div className="font-display text-lg font-semibold">
          Working hours & shifts
        </div>
        {canConfigure && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={onAssign}
              data-testid="hrms-assign-shift-button"
            >
              Assign shift
            </Button>
            <Button
              size="sm"
              onClick={onCreate}
              data-testid="hrms-create-shift-button"
              className="bg-[#255058] text-white hover:bg-[#1c3d43]"
            >
              Create shift
            </Button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Shift</TableHead>
              <TableHead>Working hours</TableHead>
              <TableHead>Break rule</TableHead>
              <TableHead>Late after</TableHead>
              <TableHead>Status</TableHead>
              {canConfigure && <TableHead>Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {shifts.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{item.name}</TableCell>
                <TableCell>
                  {item.start_time} – {item.end_time} ·{" "}
                  {item.timezone_label || item.timezone}
                </TableCell>
                <TableCell>
                  {item.allowed_breaks} breaks · {item.break_allowance_minutes}{" "}
                  min + {item.break_grace_minutes ?? 10} min grace
                </TableCell>
                <TableCell>{item.late_threshold_minutes} min</TableCell>
                <TableCell>
                  <StatusBadge value={item.status} />
                </TableCell>
                {canConfigure && (
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onEdit(item)}
                      data-testid={`hrms-edit-shift-${item.id}`}
                    >
                      Edit
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!shifts.length && (
              <TableRow>
                <TableCell
                  colSpan={canConfigure ? 6 : 5}
                  className="py-8 text-center text-sm text-muted-foreground"
                >
                  No shifts are configured in your visible scope.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function ShiftAssignmentDialog({
  open,
  options,
  saving,
  value,
  onChange,
  onClose,
  onSubmit,
}) {
  const targets =
    value.target_type === "team"
      ? options.teams
      : value.target_type === "department"
        ? options.departments
        : options.users;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign active shift</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label="Shift">
            <Select
              value={value.shift_id}
              onValueChange={(shift_id) => onChange({ ...value, shift_id })}
            >
              <SelectTrigger data-testid="hrms-assignment-shift-select">
                <SelectValue placeholder="Select shift" />
              </SelectTrigger>
              <SelectContent>
                {options.shifts?.map((shift) => (
                  <SelectItem key={shift.id} value={shift.id}>
                    {shift.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Assign by">
            <Select
              value={value.target_type}
              onValueChange={(target_type) =>
                onChange({ ...value, target_type, target_id: "" })
              }
            >
              <SelectTrigger data-testid="hrms-assignment-target-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="team">Team</SelectItem>
                <SelectItem value="department">Department</SelectItem>
                <SelectItem value="employee">Employee</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field
            label={
              value.target_type === "team"
                ? "Team"
                : value.target_type === "department"
                  ? "Department"
                  : "Employee"
            }
          >
            <Select
              value={value.target_id}
              onValueChange={(target_id) => onChange({ ...value, target_id })}
            >
              <SelectTrigger data-testid="hrms-assignment-target-select">
                <SelectValue placeholder="Select target" />
              </SelectTrigger>
              <SelectContent>
                {targets.map((target) => (
                  <SelectItem
                    key={typeof target === "string" ? target : target.id}
                    value={typeof target === "string" ? target : target.id}
                  >
                    {typeof target === "string"
                      ? target
                      : `${target.name} · ${target.email}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="hrms-assignment-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving || !value.shift_id || !value.target_id}
            data-testid="hrms-assignment-submit-button"
          >
            Assign shift
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeaveDialog({ open, value, saving, onChange, onClose, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request leave</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label="Leave type">
            <Input
              value={value.leave_type}
              onChange={(event) =>
                onChange({ ...value, leave_type: event.target.value })
              }
              data-testid="hrms-leave-type-input"
            />
          </Field>
          <Field label="Start date">
            <Input
              type="date"
              value={value.start_date}
              onChange={(event) =>
                onChange({ ...value, start_date: event.target.value })
              }
              data-testid="hrms-leave-start-input"
            />
          </Field>
          <Field label="End date">
            <Input
              type="date"
              value={value.end_date}
              onChange={(event) =>
                onChange({ ...value, end_date: event.target.value })
              }
              data-testid="hrms-leave-end-input"
            />
          </Field>
          <Field label="Reason">
            <Textarea
              value={value.reason}
              onChange={(event) =>
                onChange({ ...value, reason: event.target.value })
              }
              data-testid="hrms-leave-reason-input"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="hrms-leave-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving}
            data-testid="hrms-leave-submit-button"
          >
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RequestDialog({ open, value, saving, onChange, onClose, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Employee request</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label="Request type">
            <Select
              value={value.request_type}
              onValueChange={(request_type) =>
                onChange({ ...value, request_type })
              }
            >
              <SelectTrigger data-testid="hrms-request-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[
                  "wfh",
                  "attendance_correction",
                  "shift_change",
                  "permission",
                  "document",
                  "expense",
                  "hr",
                  "other",
                ].map((item) => (
                  <SelectItem key={item} value={item}>
                    {item.replace(/_/g, " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Request date">
            <Input
              type="date"
              value={value.request_date}
              onChange={(event) =>
                onChange({ ...value, request_date: event.target.value })
              }
              data-testid="hrms-request-date-input"
            />
          </Field>
          <Field label="Reason">
            <Textarea
              value={value.reason}
              onChange={(event) =>
                onChange({ ...value, reason: event.target.value })
              }
              data-testid="hrms-request-reason-input"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="hrms-request-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving}
            data-testid="hrms-request-submit-button"
          >
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShiftDialog({
  editing,
  open,
  value,
  saving,
  onChange,
  onClose,
  onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit shift" : "Create shift"}</DialogTitle>
        </DialogHeader>
        {!editing && (
          <Button type="button" variant="outline" size="sm" className="w-fit" onClick={() => onChange({ ...value, ...NIGHT_PRESET })} data-testid="hrms-shift-night-preset">
            Use preset: Night Shift 8 PM – 5 AM (IST)
          </Button>
        )}
        {value.start_time && value.end_time && value.end_time <= value.start_time && (
          <div className="text-xs font-medium text-amber-600" data-testid="hrms-shift-overnight-label">Overnight shift — ends the next day and counts as one shift day.</div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Shift name">
            <Input
              value={value.name}
              onChange={(event) =>
                onChange({ ...value, name: event.target.value })
              }
              data-testid="hrms-shift-name-input"
            />
          </Field>
          <Field label="Timezone">
            <Select
              value={value.timezone === "Asia/Calcutta" ? "Asia/Kolkata" : value.timezone}
              onValueChange={(timezone) => onChange({ ...value, timezone })}
            >
              <SelectTrigger data-testid="hrms-shift-timezone-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map(([zone, label]) => (
                  <SelectItem key={zone} value={zone}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Start time">
            <Input
              type="time"
              value={value.start_time}
              onChange={(event) =>
                onChange({ ...value, start_time: event.target.value })
              }
              data-testid="hrms-shift-start-input"
            />
          </Field>
          <Field label="End time">
            <Input
              type="time"
              value={value.end_time}
              onChange={(event) =>
                onChange({ ...value, end_time: event.target.value })
              }
              data-testid="hrms-shift-end-input"
            />
          </Field>
          {[
            ["allowed_breaks", "Breaks per shift", ""],
            ["break_allowance_minutes", "Total break allowance", " (minutes)"],
            ["break_grace_minutes", "Break grace", " (minutes)"],
            ["late_threshold_minutes", "Late after shift start", " (minutes)"],
          ].map(([key, label, unit]) => (
            <Field key={key} label={`${label}${unit}`}>
              <Input
                type="number"
                min="0"
                value={value[key]}
                onChange={(event) =>
                  onChange({ ...value, [key]: Number(event.target.value) })
                }
                data-testid={`hrms-shift-${key}-input`}
              />
            </Field>
          ))}
          {editing && (
            <Field label="Status">
              <Select
                value={value.status}
                onValueChange={(status) => onChange({ ...value, status })}
              >
                <SelectTrigger data-testid="hrms-shift-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="hrms-shift-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving}
            data-testid="hrms-shift-submit-button"
          >
            {editing ? "Save shift" : "Create shift"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmployeeCreateDialog({
  onChange,
  onClose,
  onSubmit,
  open,
  options,
  saving,
  value,
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent data-testid="employee-create-dialog">
        <DialogHeader>
          <DialogTitle>Add internal NEXREQ employee</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Employee ID">
            <Input
              value={value.employee_id}
              onChange={(event) =>
                onChange({ ...value, employee_id: event.target.value })
              }
              data-testid="employee-create-id-input"
            />
          </Field>
          <Field label="Internal user account">
            <Select
              value={value.user_id}
              onValueChange={(user_id) => onChange({ ...value, user_id })}
            >
              <SelectTrigger data-testid="employee-create-user-select">
                <SelectValue placeholder="Select an internal account" />
              </SelectTrigger>
              <SelectContent>
                {options.users.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name} · {item.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Designation">
            <Input
              value={value.designation}
              onChange={(event) =>
                onChange({ ...value, designation: event.target.value })
              }
              data-testid="employee-create-designation-input"
            />
          </Field>
          <Field label="Department">
            <Input
              value={value.department}
              onChange={(event) =>
                onChange({ ...value, department: event.target.value })
              }
              data-testid="employee-create-department-input"
            />
          </Field>
          <Field label="Team">
            <Select
              value={value.team_id}
              onValueChange={(team_id) => onChange({ ...value, team_id })}
            >
              <SelectTrigger data-testid="employee-create-team-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No team</SelectItem>
                {options.teams.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Shift">
            <Select
              value={value.shift_id}
              onValueChange={(shift_id) => onChange({ ...value, shift_id })}
            >
              <SelectTrigger data-testid="employee-create-shift-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No shift</SelectItem>
                {options.shifts.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Joining date">
            <Input
              type="date"
              value={value.joining_date}
              onChange={(event) =>
                onChange({ ...value, joining_date: event.target.value })
              }
              data-testid="employee-create-joining-date-input"
            />
          </Field>
          <Field label="Location">
            <Input
              value={value.location}
              onChange={(event) =>
                onChange({ ...value, location: event.target.value })
              }
              data-testid="employee-create-location-input"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="employee-create-cancel-button"
          >
            Cancel
          </Button>
          <Button
            disabled={saving || !value.employee_id.trim() || !value.user_id}
            onClick={onSubmit}
            data-testid="employee-create-submit-button"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            Create employee
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttendanceReport({ report, range, onRange, onHistory }) {
  const totals = report?.totals || {};
  return (
    <div className="space-y-4" data-testid="hrms-attendance-report">
      <Card className="p-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="font-display text-lg font-semibold">
              Attendance report
            </div>
            <div className="text-sm text-muted-foreground">
              Login, break, back, and logout history ·{" "}
              {report?.scope === "company" ? "Company-wide" : "Team / Self"}
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-xs">From</Label>
              <Input
                type="date"
                value={range.start}
                onChange={(event) =>
                  onRange({ ...range, start: event.target.value })
                }
                data-testid="hrms-report-start-input"
              />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input
                type="date"
                value={range.end}
                onChange={(event) =>
                  onRange({ ...range, end: event.target.value })
                }
                data-testid="hrms-report-end-input"
              />
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Inline label="Records" value={totals.records || 0} />
          <Inline
            label="Total worked"
            value={minutes(totals.working_minutes)}
          />
          <Inline label="Total break" value={minutes(totals.break_minutes)} />
          <Inline label="Late days" value={totals.late || 0} />
          <Inline
            label="Break overage"
            value={minutes(totals.break_overage_minutes)}
          />
        </div>
      </Card>
      <Card className="overflow-hidden" data-testid="hrms-report-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Login</TableHead>
                <TableHead>Logout</TableHead>
                <TableHead>Worked</TableHead>
                <TableHead>Break</TableHead>
                <TableHead>Overtime</TableHead>
                <TableHead>Late</TableHead>
                <TableHead>Break over</TableHead>
                <TableHead>History</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(report?.items || []).map((row) => (
                <TableRow
                  key={row.id}
                  data-testid={`hrms-report-row-${row.id}`}
                >
                  <TableCell className="font-medium">
                    {row.employee_name}
                  </TableCell>
                  <TableCell>{row.work_date}</TableCell>
                  <TableCell>
                    <StatusBadge value={row.current_status} />
                  </TableCell>
                  <TableCell>{formatTime(row.login_at)}</TableCell>
                  <TableCell>{formatTime(row.logout_at)}</TableCell>
                  <TableCell>{minutes(row.working_minutes)}</TableCell>
                  <TableCell>{minutes(row.break_minutes)}</TableCell>
                  <TableCell>{minutes(row.overtime_minutes)}</TableCell>
                  <TableCell>{minutes(row.late_minutes)}</TableCell>
                  <TableCell>{minutes(row.break_overage_minutes)}</TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onHistory(row.id)}
                      data-testid={`attendance-history-${row.id}`}
                    >
                      History
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!(report?.items || []).length && (
                <TableRow>
                  <TableCell
                    colSpan={11}
                    className="py-8 text-center text-sm text-muted-foreground"
                  >
                    No attendance records for this range.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

function AttendanceAccess({ people, onToggle }) {
  return (
    <Card className="overflow-hidden" data-testid="hrms-attendance-access">
      <div className="border-b border-border px-5 py-4">
        <div className="font-display text-lg font-semibold">
          Attendance access
        </div>
        <div className="text-sm text-muted-foreground">
          Attendance is enabled by default for every non-exempt employee. Turn
          it off for anyone who should not track Login / Break / Logout.
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Team</TableHead>
              <TableHead>Shift</TableHead>
              <TableHead>Attendance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {people.map((person) => (
              <TableRow
                key={person.id}
                data-testid={`hrms-access-row-${person.id}`}
              >
                <TableCell>
                  <div className="font-medium">{person.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {person.email}
                  </div>
                </TableCell>
                <TableCell className="capitalize">
                  {(person.role || "").replace(/_/g, " ")}
                </TableCell>
                <TableCell>{person.team_name || "—"}</TableCell>
                <TableCell>{person.shift_name || "Standard"}</TableCell>
                <TableCell>
                  <Button
                    size="sm"
                    variant={person.attendance_enabled ? "default" : "outline"}
                    onClick={() =>
                      onToggle(person.id, !person.attendance_enabled)
                    }
                    data-testid={`hrms-access-toggle-${person.id}`}
                    className={
                      person.attendance_enabled
                        ? "bg-[#255058] text-white hover:bg-[#1c3d43]"
                        : ""
                    }
                  >
                    {person.attendance_enabled ? "Enabled" : "Disabled"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!people.length && (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="py-8 text-center text-sm text-muted-foreground"
                >
                  No non-exempt employees found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function BreakTracker({ data }) {
  if (!data)
    return (
      <Card className="p-5" data-testid="hrms-break-tracker-loading">
        Loading break tracker…
      </Card>
    );
  const c = data.counts || {};
  return (
    <div className="space-y-4" data-testid="hrms-break-tracker">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric label="Team members" value={c.members} icon={UsersRound} />
        <Metric label="Active now" value={c.active} icon={BriefcaseBusiness} />
        <Metric label="On break" value={c.on_break} icon={Coffee} />
        <Metric label="Inactive" value={c.inactive} icon={Clock3} />
      </div>
      <div className="grid gap-4 xl:grid-cols-3">
        <Card
          className="overflow-hidden xl:col-span-2"
          data-testid="hrms-break-live-board"
        >
          <div className="border-b border-border px-5 py-4 font-display text-lg font-semibold">
            Live activity board · {data.work_date}
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Shift</TableHead>
                  <TableHead>Break</TableHead>
                  <TableHead>Worked</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.board.map((row) => (
                  <TableRow
                    key={row.user_id}
                    data-testid={`hrms-break-row-${row.user_id}`}
                  >
                    <TableCell>
                      <div className="font-medium">{row.employee_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {row.email}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {row.shift_name || "—"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {hm(row.break_minutes)} · {row.break_count} breaks
                    </TableCell>
                    <TableCell className="text-sm">
                      {hm(row.worked_minutes)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge value={row.status.replace(/_/g, " ")} />
                    </TableCell>
                  </TableRow>
                ))}
                {!data.board.length && (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="py-8 text-center text-sm text-muted-foreground"
                    >
                      No employees in your scope.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
        <Card className="overflow-hidden" data-testid="hrms-break-event-feed">
          <div className="border-b border-border px-5 py-4 font-display text-lg font-semibold">
            Event feed
          </div>
          <div className="max-h-[420px] divide-y divide-border overflow-y-auto">
            {data.event_feed.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between gap-3 px-5 py-3 text-sm"
              >
                <div>
                  <span className="font-medium">{item.employee_name}</span>{" "}
                  <span className="text-muted-foreground">
                    {EVENT_LABEL[item.type] || item.type}
                  </span>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatTime(item.at)}
                </span>
              </div>
            ))}
            {!data.event_feed.length && (
              <div className="px-5 py-8 text-center text-sm text-muted-foreground">
                No events today.
              </div>
            )}
          </div>
        </Card>
      </div>
      <Card className="p-5" data-testid="hrms-break-alerts">
        <div className="font-display text-lg font-semibold">
          Break alerts{" "}
          <span className="ml-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">
            {data.alerts.length}
          </span>
        </div>
        <div className="mt-3 space-y-2">
          {data.alerts.map((alert, index) => (
            <div
              key={index}
              className="flex flex-wrap items-center gap-2 rounded-md border-l-4 border-l-rose-500 bg-rose-50/60 px-4 py-2 text-sm"
              data-testid={`hrms-break-alert-${alert.user_id}-${alert.break_number}`}
            >
              <span className="font-medium">{alert.employee_name}</span>
              <span className="rounded-full bg-rose-200 px-2 py-0.5 text-xs font-semibold text-rose-800">
                {alert.running ? "Running over" : "Over limit"}
              </span>
              <span className="text-muted-foreground">
                Break #{alert.break_number} ran {hm(alert.duration_minutes)}{" "}
                (limit {alert.limit_minutes}m)
              </span>
            </div>
          ))}
          {!data.alerts.length && (
            <div className="text-sm text-muted-foreground">
              No break-limit breaches.
            </div>
          )}
        </div>
      </Card>
      <div className="grid gap-4 xl:grid-cols-2">
        <Card className="p-5" data-testid="hrms-break-on-now">
          <div className="font-display text-lg font-semibold">
            On break right now
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {data.on_break_now.map((row) => (
              <span
                key={row.user_id}
                className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800"
              >
                {row.employee_name} · since {formatTime(row.since)}
              </span>
            ))}
            {!data.on_break_now.length && (
              <div className="text-sm text-muted-foreground">
                Nobody is on break.
              </div>
            )}
          </div>
        </Card>
        <Card className="overflow-hidden" data-testid="hrms-break-per-member">
          <div className="border-b border-border px-5 py-4 font-display text-lg font-semibold">
            Per-member totals
          </div>
          <div className="max-h-80 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Breaks</TableHead>
                  <TableHead>Break time</TableHead>
                  <TableHead>Worked</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.per_member_totals.map((row) => (
                  <TableRow key={row.user_id}>
                    <TableCell className="font-medium">
                      {row.employee_name}
                    </TableCell>
                    <TableCell>{row.break_count}</TableCell>
                    <TableCell>{hm(row.break_minutes)}</TableCell>
                    <TableCell>{hm(row.worked_minutes)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </div>
  );
}

function WorkingHours({ data, range, onRange }) {
  if (!data)
    return (
      <Card className="p-5" data-testid="hrms-working-hours-loading">
        Loading working hours…
      </Card>
    );
  const s = data.summary || {};
  return (
    <div className="space-y-4" data-testid="hrms-working-hours">
      <Card className="p-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="font-display text-lg font-semibold">
              Working hours
            </div>
            <div className="text-sm text-muted-foreground">
              {s.member_days || 0} member-days · {s.members || 0} members ·{" "}
              {data.scope === "company" ? "Company-wide" : "Assigned team"}
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-xs">From</Label>
              <Input
                type="date"
                value={range.start}
                onChange={(event) =>
                  onRange({ ...range, start: event.target.value })
                }
                data-testid="hrms-wh-start-input"
              />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input
                type="date"
                value={range.end}
                onChange={(event) =>
                  onRange({ ...range, end: event.target.value })
                }
                data-testid="hrms-wh-end-input"
              />
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Inline label="Total work" value={hm(s.work_minutes)} />
          <Inline label="Avg / day" value={hm(s.avg_work_minutes)} />
          <Inline label="Total break" value={hm(s.break_minutes)} />
          <Inline label="Avg break" value={hm(s.avg_break_minutes)} />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
            Late logins: {s.late_logins || 0}
          </span>
          <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-800">
            Early logouts: {s.early_logouts || 0}
          </span>
        </div>
      </Card>
      <Card className="overflow-hidden" data-testid="hrms-wh-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Login</TableHead>
                <TableHead>Logout</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Break</TableHead>
                <TableHead>Actual work</TableHead>
                <TableHead>Flags</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((row) => (
                <TableRow key={row.id} data-testid={`hrms-wh-row-${row.id}`}>
                  <TableCell className="font-medium">
                    {row.employee_name}
                  </TableCell>
                  <TableCell>{row.work_date}</TableCell>
                  <TableCell>{formatTime(row.login_at)}</TableCell>
                  <TableCell>{formatTime(row.logout_at)}</TableCell>
                  <TableCell>{hm(row.session_minutes)}</TableCell>
                  <TableCell>{hm(row.break_minutes)}</TableCell>
                  <TableCell>{hm(row.working_minutes)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {row.late && (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                          LATE {row.late_minutes}m
                        </span>
                      )}
                      {row.early_logout && (
                        <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-800">
                          EARLY
                        </span>
                      )}
                      {!row.late && !row.early_logout && (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!data.rows.length && (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="py-8 text-center text-sm text-muted-foreground"
                  >
                    No working-hours records for this range.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

const EVENT_LABEL = {
  login: "logged in",
  break: "started a break",
  back: "resumed work",
  logout: "logged out",
};

function hm(value) {
  const total = Math.max(0, Math.round(value || 0));
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  return hours ? `${hours}h ${mins}m` : `${mins}m`;
}

function Payroll({ payslips, saving, onRun, canRun, onHistory }) {
  const downloadPayslip = async (slip) => {
    try {
      const res = await api.get(`/hrms/payroll/payslips/${slip.id}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `payslip_${slip.employee_name}_${slip.period_start}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(apiErr(error));
    }
  };
  const [form, setForm] = useState({
    period_start: "",
    period_end: "",
    expected_working_days: 22,
    prorate_by_attendance: true,
  });
  const totals = payslips.reduce(
    (acc, slip) => ({
      gross: acc.gross + (slip.gross_pay || 0),
      net: acc.net + (slip.net_pay || 0),
    }),
    { gross: 0, net: 0 },
  );
  const money = (value) => `$${Number(value || 0).toLocaleString()}`;
  return (
    <div className="space-y-4" data-testid="hrms-payroll">
      <Card
        className="p-5"
        data-testid="hrms-payroll-run-card"
        style={{ display: canRun ? "block" : "none" }}
      >
        <div className="font-display text-lg font-semibold">Run payroll</div>
        <div className="mt-1 text-sm text-muted-foreground">
          Calculates each active employee's pay for the period using their
          attendance (present days, worked hours, overtime) and approved leave.
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <Label className="text-xs">Period start</Label>
            <Input
              type="date"
              value={form.period_start}
              onChange={(event) =>
                setForm({ ...form, period_start: event.target.value })
              }
              data-testid="payroll-start-input"
            />
          </div>
          <div>
            <Label className="text-xs">Period end</Label>
            <Input
              type="date"
              value={form.period_end}
              onChange={(event) =>
                setForm({ ...form, period_end: event.target.value })
              }
              data-testid="payroll-end-input"
            />
          </div>
          <div>
            <Label className="text-xs">Expected working days</Label>
            <Input
              type="number"
              min="1"
              max="31"
              value={form.expected_working_days}
              onChange={(event) =>
                setForm({
                  ...form,
                  expected_working_days: Number(event.target.value),
                })
              }
              data-testid="payroll-expected-days-input"
            />
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              variant={form.prorate_by_attendance ? "default" : "outline"}
              onClick={() =>
                setForm({
                  ...form,
                  prorate_by_attendance: !form.prorate_by_attendance,
                })
              }
              data-testid="payroll-prorate-toggle"
              className={
                form.prorate_by_attendance
                  ? "w-full bg-[#255058] text-white hover:bg-[#1c3d43]"
                  : "w-full"
              }
            >
              Pro-rate by attendance:{" "}
              {form.prorate_by_attendance ? "On" : "Off"}
            </Button>
          </div>
          <div className="flex items-end">
            <Button
              onClick={() => {
                if (!form.period_start || !form.period_end) {
                  toast.error("Select a payroll period");
                  return;
                }
                onRun(form);
              }}
              disabled={saving}
              data-testid="payroll-run-button"
              className="w-full bg-[#255058] text-white hover:bg-[#1c3d43]"
            >
              {saving ? "Calculating…" : "Calculate payroll"}
            </Button>
          </div>
        </div>
      </Card>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Inline label="Payslips" value={payslips.length} />
        <Inline label="Total gross" value={money(totals.gross)} />
        <Inline label="Total net" value={money(totals.net)} />
        <Inline
          label="Latest period"
          value={
            payslips[0]
              ? `${payslips[0].period_start} → ${payslips[0].period_end}`
              : "—"
          }
        />
      </div>
      <Card className="overflow-hidden" data-testid="hrms-payroll-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Present / Leave / Absent</TableHead>
                <TableHead>Worked</TableHead>
                <TableHead>Base payable</TableHead>
                <TableHead>Overtime</TableHead>
                <TableHead>Deductions</TableHead>
                <TableHead>Gross</TableHead>
                <TableHead>Net</TableHead>
                <TableHead>Payslip</TableHead>
                <TableHead>History</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payslips.map((slip) => (
                <TableRow key={slip.id} data-testid={`hrms-payslip-${slip.id}`}>
                  <TableCell className="font-medium">
                    {slip.employee_name}
                  </TableCell>
                  <TableCell className="text-xs">
                    {slip.period_start} → {slip.period_end}
                  </TableCell>
                  <TableCell className="text-sm">
                    {slip.present_days ?? 0} / {slip.leave_days ?? 0} /{" "}
                    {slip.absent_days ?? 0}
                    {slip.prorated && (
                      <span className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-semibold text-amber-800">
                        {Math.round((slip.attendance_factor || 1) * 100)}%
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {slip.worked_hours ?? 0}h
                  </TableCell>
                  <TableCell>
                    {money(slip.base_payable ?? slip.base_salary)}
                  </TableCell>
                  <TableCell>{money(slip.overtime_amount)}</TableCell>
                  <TableCell>{money(slip.deductions)}</TableCell>
                  <TableCell className="font-medium">
                    {money(slip.gross_pay)}
                  </TableCell>
                  <TableCell className="font-semibold text-[#255058]">
                    {money(slip.net_pay)}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadPayslip(slip)}
                      data-testid={`payslip-pdf-${slip.id}`}
                    >
                      <FileText className="mr-1 h-3.5 w-3.5" /> PDF
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onHistory(slip.id)}
                      data-testid={`payroll-history-${slip.id}`}
                    >
                      History
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!payslips.length && (
                <TableRow>
                  <TableCell
                    colSpan={11}
                    className="py-8 text-center text-sm text-muted-foreground"
                  >
                    No payslips yet. Run payroll for a period to generate them.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

function Metric({ icon: Icon, label, value, onClick }) {
  return (
    <Card
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => onClick && e.key === "Enter" && onClick()}
      className={`border-l-4 border-l-[#255058] p-4 ${onClick ? "cursor-pointer transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-md" : ""}`}
      data-testid={`hrms-metric-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{label}</span>
        <Icon className="h-4 w-4 text-[#255058]" />
      </div>
      <div className="mt-2 font-display text-2xl font-bold">{value ?? 0}</div>
    </Card>
  );
}
function Inline({ label, value, onClick }) {
  return (
    <div
      className={`border-l border-border pl-3 ${onClick ? "cursor-pointer rounded-sm transition-colors hover:bg-muted/60" : ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      data-testid={onClick ? `hrms-overview-${label.toLowerCase().replace(/[^a-z]+/g, "-")}` : undefined}
    >
      <div className="font-display text-lg font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
function Field({ children, label }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
function minutes(value) {
  const total = value || 0;
  const hours = String(Math.floor(total / 60)).padStart(2, "0");
  const minutesPart = String(total % 60).padStart(2, "0");
  return `${hours}:${minutesPart}`;
}
function labelForTab(value) {
  return {
    overview: "Overview",
    employees: "Employees",
    leave_requests: "Leave & Requests",
    break_tracker: "Break Tracker",
    working_hours: "Working Hours",
    payroll: "Payroll",
    reports: "Reports",
    attendance_access: "Attendance access",
    shifts: "Shifts",
    team_activity: "Team activity",
  }[value];
}

function formatTime(value) {
  return value
    ? new Date(value).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
}
